import type { EvidenceItem } from "@/features/audit/types";

export type Severity = "critical" | "high" | "medium" | "low";
export type Variant = "A" | "B";
export type Agreement = "agree" | "partial" | "disagree" | "unscorable";

export const SEVERITIES: Severity[] = ["critical", "high", "medium", "low"];

/**
 * One sampled card from /v1/learning/queue: captured media (evidence inline) +
 * what OUR engine said. `our` is null when we found no deficiency (a clean
 * sample) — "no deficiency" is a first-class answer, not always wrong.
 */
export interface QueueItem {
  id: string; // = sample_key (stable media identity; the server re-derives it on submit)
  sample_kind: "survey_clip" | "equipment_photo";
  property_id: string;
  asset_id: string | null;
  survey_field_id: string | null;
  our_is_deficiency: boolean;
  our: { rule_id: string; severity: Severity | null; title: string; detail: string | null } | null;
  /** Clean equipment photos only: echoed back on submit for server-side validation. */
  document_id?: string;
  /** Survey samples answerable in the question's own vocabulary (severity is
   * then derived server-side from the answer via the rule pack). */
  question?: { title: string; text: string | null; answer_kind: "boolean" | "count" } | null;
  /** May be absent on a card with no media, or from a backend not serving
   *  evidence inline yet — consumers must handle undefined. */
  evidence?: EvidenceItem[];
  context: string;
}

export interface Progress {
  points: number;
  current_streak: number;
  longest_streak: number;
  labels_count: number;
  badges: unknown[];
  last_active_at: string | null;
}

export interface SubmitBody {
  sampleKey: string; // informational — the server re-derives the key from the media identity
  sampleKind: "survey_clip" | "equipment_photo";
  propertyId: string;
  assetId?: string | null;
  surveyFieldId?: string | null;
  documentId?: string | null; // clean equipment photos — server-validated
  variant: Variant; // informational — the server re-resolves the assigned arm
  ourAnswerShown: boolean;
  clientVerdict?: "agree" | "severity_diff" | "reject";
  clientSeverity?: Severity;
  clientIsDeficiency?: boolean;
  clientCantAssess?: boolean;
  /** Answer in the question's own vocabulary — server derives severity from it. */
  clientAnswer?: string | number;
  reason?: string;
}

export interface SubmitResult {
  agreement: Agreement;
  reward: {
    points_awarded: number;
    total_points: number;
    current_streak: number;
    labels_count: number;
    new_badges: string[];
  };
}
